import { Directive, ElementRef, HostListener, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Diretiva para aplicar máscara de placa no formato Mercosul
 * Formato: ABC1D23 ou ABC-1D23
 * - 3 letras + 1 número + 1 letra + 2 números
 * - Converte automaticamente para maiúsculas
 * - Valida o formato durante a digitação
 */
@Directive({
  selector: '[appPlateMask]'
})
export class PlateMaskDirective implements OnInit {
  private previousValue: string = '';

  constructor(
    private el: ElementRef,
    private control: NgControl
  ) {}

  ngOnInit(): void {
    // Formata o valor inicial se existir
    const initialValue = this.control.value;
    if (initialValue) {
      setTimeout(() => this.applyMask(initialValue), 0);
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove caracteres não permitidos (mantém apenas letras, números e hífen)
    value = value.replace(/[^a-zA-Z0-9\-]/g, '');
    
    // Converte para maiúsculas
    value = value.toUpperCase();

    // Remove hífens extras (apenas um hífen é permitido)
    const parts = value.split('-');
    if (parts.length > 2) {
      value = parts[0] + '-' + parts.slice(1).join('');
    }

    // Aplica a máscara do formato Mercosul
    value = this.formatMercosul(value);

    // Atualiza o campo
    input.value = value;
    this.control.control?.setValue(value, { emitEvent: false });

    // Valida o formato
    this.validatePlate(value);

    this.previousValue = value;
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (value) {
      // Remove o hífen se existir para validação
      const cleanValue = value.replace('-', '');
      
      // Valida o formato final
      if (this.isValidMercosulPlate(cleanValue)) {
        // Formata com hífen: ABC-1D23
        value = this.formatWithHyphen(cleanValue);
        input.value = value;
        this.control.control?.setValue(value, { emitEvent: false });
        this.control.control?.setErrors(null);
      } else {
        // Se não for válido, marca erro
        this.control.control?.setErrors({ invalidPlate: true });
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    
    // Limpa e formata o texto colado
    let cleanValue = pastedText.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    // Limita ao tamanho máximo (7 caracteres)
    if (cleanValue.length > 7) {
      cleanValue = cleanValue.substring(0, 7);
    }

    // Aplica a máscara
    const formatted = this.formatMercosul(cleanValue);
    
    const input = this.el.nativeElement as HTMLInputElement;
    input.value = formatted;
    this.control.control?.setValue(formatted, { emitEvent: false });
    
    this.validatePlate(formatted);
  }

  private formatMercosul(value: string): string {
    // Remove hífen se existir
    value = value.replace('-', '');

    // Limita ao tamanho máximo (7 caracteres)
    if (value.length > 7) {
      value = value.substring(0, 7);
    }

    // Formato Mercosul: ABC1D23
    // Posição 0-2: letras
    // Posição 3: número
    // Posição 4: letra
    // Posição 5-6: números

    let formatted = '';
    
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      
      if (i < 3) {
        // Primeiras 3 posições: apenas letras
        if (/[A-Z]/.test(char)) {
          formatted += char;
        }
      } else if (i === 3) {
        // 4ª posição: apenas número
        if (/[0-9]/.test(char)) {
          formatted += char;
        }
      } else if (i === 4) {
        // 5ª posição: apenas letra
        if (/[A-Z]/.test(char)) {
          formatted += char;
        }
      } else if (i >= 5 && i < 7) {
        // 6ª e 7ª posições: apenas números
        if (/[0-9]/.test(char)) {
          formatted += char;
        }
      }
    }

    return formatted;
  }

  private formatWithHyphen(value: string): string {
    // Formata ABC1D23 para ABC-1D23
    if (value.length === 7) {
      return `${value.substring(0, 3)}-${value.substring(3)}`;
    }
    return value;
  }

  private isValidMercosulPlate(value: string): boolean {
    // Remove hífen se existir
    value = value.replace('-', '');
    
    // Valida formato Mercosul: 3 letras + 1 número + 1 letra + 2 números
    const mercosulPattern = /^[A-Z]{3}[0-9]{1}[A-Z]{1}[0-9]{2}$/;
    return mercosulPattern.test(value);
  }

  private validatePlate(value: string): void {
    const cleanValue = value.replace('-', '');
    
    if (cleanValue.length === 0) {
      // Campo vazio - remove erro
      this.control.control?.setErrors(null);
    } else if (cleanValue.length < 7) {
      // Ainda está digitando - não marca erro
      // Apenas marca como inválido se já tiver 7 caracteres mas formato errado
      if (this.control.control?.hasError('invalidPlate')) {
        this.control.control?.setErrors(null);
      }
    } else if (cleanValue.length === 7 && !this.isValidMercosulPlate(cleanValue)) {
      // Formato completo mas inválido
      this.control.control?.setErrors({ invalidPlate: true });
    } else {
      // Formato válido
      this.control.control?.setErrors(null);
    }
  }

  private applyMask(value: string): void {
    if (!value) return;
    
    const input = this.el.nativeElement as HTMLInputElement;
    let cleanValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    const formatted = this.formatMercosul(cleanValue);
    input.value = formatted;
    this.control.control?.setValue(formatted, { emitEvent: false });
  }
}
